import {
  AfterViewInit,
  Component,
  inject,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { MatPaginator } from '@angular/material/paginator';
import { MatDialog } from '@angular/material/dialog';

import { CentrexSdaModel } from '../../models/centrex-sda.model';
import { CentrexSdasService } from 'src/app/services/centrex-sdas.service';
import { SdasFormComponent } from './sdas-form/sdas-form.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { CentrexExtensionsService } from 'src/app/services/centrex-extensions.service';
import { AccessRightsService } from 'src/app/services/access-rights.service';
import { OrganizationsService } from 'src/app/services/organization.service';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { finalize } from 'rxjs';
import { PlatformService } from 'src/app/services/platform.service';

@Component({
  selector: 'app-sdas',
  templateUrl: './sdas.component.html',
  styleUrls: ['./sdas.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatPaginator,
    FormsModule,
    ReactiveFormsModule,
    MatTableModule,
    NgxSpinnerModule,
  ],
})
export class SdasComponent implements OnInit, AfterViewInit {
  displayedColumns: string[] = ['number', 'target', 'targetType', 'actions'];
  dataSource = new MatTableDataSource<CentrexSdaModel>();
  filtersForm!: FormGroup;

  organizationId: string | null = null;
  platform: string = this.platformService.getCurrentPlatform();

  isFullAccess = false;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  private spinner = inject(NgxSpinnerService);

  constructor(
    private fb: FormBuilder,
    private sdasService: CentrexSdasService,
    private dialog: MatDialog,
    private extensionsService: CentrexExtensionsService,
    private accessRightsService: AccessRightsService,
    private organizationsService: OrganizationsService,
    private platformService: PlatformService,
  ) {}

  ngOnInit(): void {
    this.filtersForm = this.fb.group({
      number: [''],
      target: [''],
      targetType: [''],
    });

    // Accès complet
    this.accessRightsService
      .hasNexusCentrexFullAccess()
      .subscribe((isAllowed) => (this.isFullAccess = isAllowed));

    // Écoute des changements d'organisation
    this.organizationsService.currentOrganizationChange$.subscribe((org) => {
      if (org) {
        this.organizationId = org.id;
        this.loadSdas();
      }
    });

    // // Organisation par défaut
    // const currentOrg = this.organizationsService.getCurrentOrganization();
    // if (currentOrg) {
    //   this.organizationId = currentOrg.id;
    //   this.loadSdas();
    // }
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
  }

  onPlatformChange(): void {
  this.platformService.setPlatform(this.platform);
  this.loadSdas();
}

  loadSdas(): void {
    if (!this.organizationId) return;

    this.spinner.show('spinner');

    this.sdasService
      .getSdas(this.organizationId, this.platform)
      .pipe(
        finalize(() => {
          this.spinner.hide('spinner');
        }),
      )
      .subscribe((sdas) => {
        this.dataSource = new MatTableDataSource(sdas);

        this.dataSource.sort = this.sort;
        this.dataSource.paginator = this.paginator;

        this.applyFilters();
      });
  }

  applyFilters(): void {
    const values = this.filtersForm.value;
    this.dataSource.filterPredicate = (data, filter): boolean => {
      const f = JSON.parse(filter);
      return (
        (!f.number ||
          data.number?.toLowerCase().includes(f.number.toLowerCase()) ===
            true) &&
        (!f.target ||
          data.target?.toLowerCase().includes(f.target.toLowerCase()) ===
            true) &&
        (!f.targetType ||
          data.targetType
            ?.toLowerCase()
            .includes(f.targetType.toLowerCase()) === true)
      );
    };
    this.dataSource.filter = JSON.stringify(values);
  }

  resetColumnFilter(column: string): void {
    this.filtersForm.get(column)?.setValue('');
    this.applyFilters();
  }

  onView(sda: CentrexSdaModel): void {
    this.dialog.open(SdasFormComponent, {
      width: '900px',
      data: {
        sda,
        organizationId: this.organizationId,
        platform: this.platform,
        mode: 'view',
      },
    });
  }

  onEdit(sda: CentrexSdaModel): void {
    if (!this.isFullAccess) return;
    this.dialog
      .open(SdasFormComponent, {
        width: '900px',
        data: {
          sda,
          organizationId: this.organizationId,
          platform: this.platform,
          mode: 'edit',
        },
      })
      .afterClosed()
      .subscribe((result) => {
        if (result === 'refresh') {
          this.loadSdas();
        }
      });
  }
}
